import ReactJsonView from '@microlink/react-json-view'
import { Eye, FileDown, Loader2, TimerReset } from 'lucide-react'
import React from 'react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Badge, CollapsibleContainer } from 'slab'
import { getFormattedDate } from 'utils'
import { useGetBrokersTasksByIdTaskIdResultsResultId } from 'utils/web_api'
import { type BrokersTaskResult } from 'utils/web_api.schemas'

const getStatusBadgeType = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'failed':
    case 'aborted':
      return 'critical'
    case 'processing':
      return 'info'
    case 'ignored':
    case 'failsafe':
      return 'neutral'
    default:
      return 'neutral'
  }
}

const formatDuration = (durationMs: number) => {
  if (durationMs < 1000) {
    return `${durationMs} ms`
  }

  const seconds = durationMs / 1000
  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainder = (seconds % 60).toFixed(1)
  return `${minutes}m ${remainder}s`
}

const RESULT_POLLING_INTERVAL_MS = 3000

type TaskResultExplorerProps = {
  taskId: string
  results: BrokersTaskResult[]
  isLoading: boolean
  collapseVersion: number
}

const TaskResultExplorer = ({ taskId, results, isLoading }: TaskResultExplorerProps) => {
  const [openResults, setOpenResults] = React.useState<Record<string, boolean>>({})

  const handleResultOpenChange = (resultId: string, open: boolean) => {
    setOpenResults((previous) => ({ ...previous, [resultId]: open }))
  }

  if (isLoading) {
    return (
      <div className='py-8'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  if (!results || results.length === 0) {
    return (
      <div className='rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600'>
        No task results recorded yet.
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      <div className='max-h-[50vh] space-y-3 overflow-y-auto pr-1'>
        {results.map((result) => {
          const isOpen = openResults[result.id] ?? false

          return (
            <TaskResultCard
              key={result.id}
              taskId={taskId}
              result={result}
              isOpen={isOpen}
              onOpenChange={(open) => handleResultOpenChange(result.id, open)}
            />
          )
        })}
      </div>
    </div>
  )
}

type TaskResultCardProps = {
  taskId: string
  result: BrokersTaskResult
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const TaskResultCard = ({ taskId, result, isOpen, onOpenChange }: TaskResultCardProps) => {
  const headingContent = (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center gap-2'>
        <span className='font-semibold text-gray-900'>{result.description}</span>
        <Badge type={getStatusBadgeType(result.status)}>
          {result.status.replace(/_/g, ' ')}
        </Badge>
      </div>
      <div className='flex flex-wrap items-center gap-2 text-xs text-gray-500'>
        <span className='flex items-center gap-1'>
          <TimerReset className='h-3.5 w-3.5 text-gray-400' />
          {formatDuration(result.duration)}
        </span>
        <span>
          Started {getFormattedDate(result.start_time, { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>
    </div>
  )

  const detailQuery = useGetBrokersTasksByIdTaskIdResultsResultId(
    taskId,
    result.id,
    {
      query: {
        enabled: isOpen,
        refetchInterval: (query) => {
          const status =
            (query.state.data as BrokersTaskResult | undefined)?.status ?? result.status
          return status === 'processing' ? RESULT_POLLING_INTERVAL_MS : false
        },
        refetchIntervalInBackground: false,
      },
    }
  )

  const detail = detailQuery.data
  const outputs = detail?.outputs ?? result.outputs ?? {}
  const attachments = detail?.attachments ?? result.attachments ?? []
  const steps = detail?.steps ?? []

  return (
    <CollapsibleContainer
      heading={headingContent}
      headingIconContent={
        <div className='flex items-center gap-1'>
          {detailQuery.isFetching && (
            <Loader2 className='h-3.5 w-3.5 animate-spin text-gray-400' />
          )}
          <Badge type='neutral'>{result.id.slice(-6)}</Badge>
        </div>
      }
      headingLevel={4}
      defaultOpen={false}
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      {isOpen &&
        <div className='space-y-4 p-4'>
          {result.message && (
            <div>
              <p className='text-sm text-gray-700'>{result.message}</p>
            </div>
          )}

          <div>
            <h5 className='text-xs uppercase tracking-wide text-gray-500'>
              Outputs
            </h5>
            <div className='mt-2 rounded border border-gray-200 bg-gray-50 p-3 text-xs'>
              <ReactJsonView
                src={outputs}
                theme={'github' as any}
                collapsed={1}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
              />
            </div>
          </div>

          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <h5 className='text-xs uppercase tracking-wide text-gray-500'>
                Steps
              </h5>
              <div className='mt-2 space-y-3'>
                {detailQuery.isLoading
                  ? (<div className='py-3'><LoadingSpinnerCentered /></div>)
                  : !steps.length
                      ? (
                        <div className='rounded border border-dashed border-gray-200 p-3 text-xs text-gray-500'>
                          No steps recorded for this attempt.
                        </div>
                        )
                      : (
                          steps.map((step, index) => (
                            <div key={`${result.id}-step-${index}`} className='rounded border border-gray-200 bg-white p-3 text-sm'>
                              <div className='flex items-center justify-between'>
                                <div className='font-medium text-gray-800'>
                                  {step.comment || step.type || 'Step'}
                                </div>
                                <Badge type={getStatusBadgeType(step.status)}>
                                  {step.status}
                                </Badge>
                              </div>
                              <div className='mt-1 text-xs text-gray-500'>
                                {step.message}
                              </div>
                              <div className='mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500'>
                                <span>{formatDuration(step.duration)}</span>
                                <span>
                                  {getFormattedDate(step.start_time, { hour: 'numeric', minute: '2-digit' })}
                                </span>
                              </div>
                              {step.result && (
                                <div className='mt-2 rounded bg-gray-50 p-2 text-xs'>
                                  <pre className='whitespace-pre-wrap break-all text-gray-700'>
                                    {JSON.stringify(step.result, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {step.error && (
                                <div className='mt-2 rounded bg-red-50 p-2 text-xs text-red-700'>
                                  {step.error}
                                </div>
                              )}
                            </div>
                          ))
                        )}
              </div>
            </div>

            <div>
              <h5 className='text-xs uppercase tracking-wide text-gray-500'>
                Attachments
              </h5>
              <div className='mt-2 space-y-3'>
                {attachments.length === 0
                  ? (
                    <div className='rounded border border-dashed border-gray-200 p-3 text-xs text-gray-500'>
                      No attachments for this attempt.
                    </div>
                    )
                  : (
                      attachments.map((attachment) => (
                        <div key={attachment.id} className='space-y-1 rounded border border-gray-200 bg-white p-3 text-sm'>
                          <div className='flex items-center gap-2 text-gray-800'>
                            <Eye className='h-4 w-4 text-gray-400' />
                            <a
                              href={attachment.preview_url}
                              target='_blank'
                              rel='noreferrer'
                              className='font-medium text-blue-600 hover:text-blue-500'
                            >
                              Preview {attachment.filename}
                            </a>
                          </div>
                          <div className='flex items-center gap-2 text-gray-800'>
                            <FileDown className='h-4 w-4 text-gray-400' />
                            <a
                              href={attachment.download_url}
                              target='_blank'
                              rel='noreferrer'
                              className='font-medium text-blue-600 hover:text-blue-500'
                            >
                              Download
                            </a>
                          </div>
                          <div className='text-xs text-gray-500'>
                            {attachment.content_type} · {getFormattedDate(attachment.created_at, { hour: 'numeric', minute: '2-digit' })}
                            {attachment.byte_size ? ` · ${Math.round(attachment.byte_size / 1024)} KB` : ''}
                          </div>
                        </div>
                      ))
                    )}
              </div>
            </div>
          </div>
        </div>}
    </CollapsibleContainer>
  )
}

export default TaskResultExplorer
